import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Save, X, Check } from 'lucide-react';
import OrganizationTab from '../components/settings/OrganizationTab';
import VoiceAgentTab from '../components/settings/VoiceAgentTab';
import UsersRolesTab from '../components/settings/UsersRolesTab';
import { useUser } from '../hooks/useUser';
import useRestaurants from '../hooks/useRestaurants';
import { useSettings } from '../contexts/SettingsContext';
// import IntegrationsTab from '../components/settings/IntegrationsTab'; // Commented out for now

/**
 * SettingsPage Component
 * 
 * Main settings page with tabbed navigation for managing restaurant preferences,
 * AI behavior, users, and integrations.
 */
const SettingsPage = () => {
  const { isHubkomClient: isHubkomFromAuth } = useUser();
  const { restaurants } = useRestaurants();
  const { selectedLocationId } = useSettings();
  const selectedRestaurant = restaurants?.find(r => r.locationId === selectedLocationId);
  const isHubkomClient = isHubkomFromAuth || selectedRestaurant?.isHubkomClient === true;
  const [activeTab, setActiveTab] = useState('organization');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saved' | 'error' | null
  const saveTimeoutRef = useRef(null);

  // Track original values to detect changes
  const [originalValues, setOriginalValues] = useState({});
  const [currentValues, setCurrentValues] = useState({});

  // Initialize default values
  useEffect(() => {
    // This will be populated as we build each tab
    const defaults = {
      organization: {},
      voiceAgent: {},
      usersRoles: {},
      integrations: {},
    };
    setOriginalValues(defaults);
    setCurrentValues(defaults);
  }, []);

  // Check for unsaved changes
  useEffect(() => {
    const hasChanges = JSON.stringify(currentValues) !== JSON.stringify(originalValues);
    setHasUnsavedChanges(hasChanges);
    
    // Clear save status when changes are made
    if (hasChanges && saveStatus === 'saved') {
      setSaveStatus(null);
    }
  }, [currentValues, originalValues, saveStatus]);

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus(null);

    try {
      // TODO: Replace with actual API call when backend is ready
      // For now, simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update original values to current values
      setOriginalValues(JSON.parse(JSON.stringify(currentValues)));
      setHasUnsavedChanges(false);
      setSaveStatus('saved');

      // Clear status message after 3 seconds
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    // Reset to original values
    setCurrentValues(JSON.parse(JSON.stringify(originalValues)));
    setHasUnsavedChanges(false);
    setSaveStatus(null);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="p-4 lg:p-8 min-h-screen pb-24">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl text-white mb-2">Settings</h1>
          <p className="text-gray-400">
            Manage your location's preferences, AI behavior, users, and integrations.
          </p>
        </div>

          {/* Main Content */}
          <Card className="bg-gray-900 border-gray-700">
            <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="border-b border-gray-700 px-6 pt-6 pb-0">
                <TabsList className={`bg-gray-800 border-gray-700 w-full h-auto p-1.5 grid gap-1.5 ${isHubkomClient ? 'grid-cols-1' : 'grid-cols-3'}`}>
                  <TabsTrigger
                    value="organization"
                    className="data-[state=active]:bg-[#3AD0E6] data-[state=active]:text-white px-4 py-2.5 text-sm font-medium"
                  >
                    Organization
                  </TabsTrigger>
                  {!isHubkomClient && (
                    <TabsTrigger
                      value="voice-agent"
                      className="data-[state=active]:bg-[#3AD0E6] data-[state=active]:text-white px-4 py-2.5 text-sm font-medium"
                    >
                      Voice Agent
                    </TabsTrigger>
                  )}
                  {!isHubkomClient && (
                    <TabsTrigger
                      value="users-roles"
                      className="data-[state=active]:bg-[#3AD0E6] data-[state=active]:text-white px-4 py-2.5 text-sm font-medium"
                    >
                      Users & Roles
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>

              <div className="p-6">
                <TabsContent value="organization" className="mt-0">
                  <OrganizationTab />
                </TabsContent>
                <TabsContent value="voice-agent" className="mt-0">
                  <VoiceAgentTab />
                </TabsContent>
                <TabsContent value="users-roles" className="mt-0">
                  <UsersRolesTab />
                </TabsContent>
                {/* Integrations tab - commented out for now */}
                {/* <TabsContent value="integrations" className="mt-0">
                  <IntegrationsTab />
                </TabsContent> */}
              </div>
            </Tabs>
            </CardContent>
          </Card>

        {/* Sticky Save Changes Button */}
        {hasUnsavedChanges && (
          <div className="fixed bottom-6 right-6 z-50">
            <Card className="bg-gray-900 border-gray-700 shadow-2xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  {saveStatus === 'saved' && (
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <Check className="w-4 h-4" />
                      <span>Saved just now</span>
                    </div>
                  )}
                  {saveStatus === 'error' && (
                    <div className="flex items-center gap-2 text-red-400 text-sm">
                      <X className="w-4 h-4" />
                      <span>Error saving</span>
                    </div>
                  )}
                  {!saveStatus && (
                    <>
                      <Button
                        variant="outline"
                        onClick={handleCancel}
                        className="border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
                      >
                        {isSaving ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        </div>
      </div>
  );
};

export default SettingsPage;

